import { Datetime, Validation, zeroPadFlightNumber } from '@/utils';
import { TrackFlightTypeEnum, Trip, TripStatusEnum } from '@/models/gen/graphql';
import { useEffect, useMemo } from 'react';

import FormButton from '@/components/FormButton';
import SimpleTableCell from '@/components/SimpleTable/SimpleTableCell';
import { getClasses } from '@/utils/strings';
import useIsVisible from '@/hooks/useIsVisible';
import useSession from '@/state/session';
import { useSimpleTableRowContext } from '@/components/SimpleTable/SimpleTableRow';
import { useTripEtaPopupState } from '@/features/Trips/components/TripEtaPopup';

const FlightNumberCell = () => {
  const {
    data: {
      id,
      driverId,
      scheduled,
      flightNumber,
      completionId,
      loopName,
      offset,
      trackFlight,
      status,
      deletedAt,
      puCompletedDatetime,
    } = {},
  } = useSimpleTableRowContext<Trip>();
  const [tripId, setTripEtaPopupState] = useTripEtaPopupState(({ state, setState }) => [state.tripId, setState]);
  const getTripEtaAllowedWindowDurationBeforePickupMinutes = useSession(
    ({ state }) => state.config.getTripEtaAllowedWindowDurationBeforePickupMinutes
  );
  const showTripEtaIcon = useMemo(() => {
    // we'll only get the eta for trips that are active & not completed & not deleted
    if (
      completionId ||
      puCompletedDatetime ||
      !driverId ||
      status !== TripStatusEnum.Active ||
      deletedAt ||
      !scheduled ||
      !Validation.isNumber(getTripEtaAllowedWindowDurationBeforePickupMinutes)
    ) {
      return false;
    }

    const etaWindowDatetime = new Datetime().asUtc().applyOffset(offset).add(getTripEtaAllowedWindowDurationBeforePickupMinutes, 'minutes');
    const output = etaWindowDatetime.isAfter(scheduled, 'minutes') || etaWindowDatetime.isSame(scheduled, 'minutes');
    return output;
  }, [id, driverId, completionId, status, scheduled, puCompletedDatetime, offset, getTripEtaAllowedWindowDurationBeforePickupMinutes]);

  const [ref, isVisible] = useIsVisible(undefined, { rootMargin: '-45px 0px 0px 0px' });

  useEffect((): void => {
    if (!showTripEtaIcon || isVisible || tripId !== id) return;
    setTripEtaPopupState((current) => ({
      ...current,
      target: undefined,
      tripId: undefined,
    }));
  }, [isVisible, showTripEtaIcon, ref, setTripEtaPopupState, tripId, id]);

  return (
    <SimpleTableCell
      name="FLIGHT_NUMBER"
      className={getClasses(
        'd-flex align-items-center gap-2',
        trackFlight?.type === TrackFlightTypeEnum.International ? 'bg-purple bg-opacity-15 text-dark' : undefined
      )}
    >
      {showTripEtaIcon && (
        <FormButton
          variant="icon"
          icon={<i className="sv sv-clock" ref={ref} />}
          onClick={(event) =>
            setTripEtaPopupState((current) => ({
              ...current,
              target: current.tripId === id ? undefined : (event.target as HTMLElement),
              tripId: current.tripId === id ? undefined : id,
            }))
          }
        />
      )}
      <span>{loopName || formatFlightNumber(flightNumber)}</span>
    </SimpleTableCell>
  );
};

export const formatFlightNumber = (flightNumber: number) => {
  return flightNumber !== null && flightNumber >= 0 ? zeroPadFlightNumber(flightNumber) : '--';
};

export default FlightNumberCell;
